import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import backend from '@api/backend'
import {
  notifyApiError,
  notifySuccess,
} from '@common/utils/notify'
import { mapUser } from '@common/utils/mapUser'
import { hasPermissions } from '@common/utils/validation/authValidators'
import Account from '@data/accounts/model/account'
import CustomerLocation, { AlertSub } from '@data/locations/model/customerLocation'
import User, {
  ClientUserRole,
  Group,
  RawUserResponse,
  UserAccountAssoc,
  UserInvite,
  UserInviteAccounts,
} from '@data/users/model/user'
import {
  InvitationResponse,
  mapInvitation,
} from './mapInvitation'
import {
  InviteLocation,
  UserInviteLocation,
  UserLocation,
} from './model/userManagement'
import { useAuthStore } from '@store/auth.store'

export const USERS_QUERY_KEY = ['users'] as const
export const ACCOUNT_USERS_QUERY_KEY = ['account-users'] as const
export const ACCOUNT_INVITATIONS_QUERY_KEY = ['account-invitations'] as const
export const USER_ALERT_SUBS_QUERY_KEY = ['user-alert-subs'] as const
export const USER_QUERY_KEY = ['user'] as const

interface UserSummaryResponse {
  id: number | string
  email: string
  active: boolean
  firstName?: string | null
  lastName?: string | null
}

// Deliberately snake_case, and not an oversight: GET /users/{id}/alert_subs declares no
// response model, so the spec shows a null body. The route returns
// `UsageAlertUserLocationsInDB`, which has not been recased — its serialization keys are
// still `user_id`, `location_id` and `recipient_type`. Do not "fix" these to camelCase.
interface AlertSubscriptionResponse {
  id: string
  user_id: string
  email: string
  location_id: string
  recipient_type: string
}

// `UserAccountAssoc` — the body of POST /users and PUT|PATCH /users/{id}/accounts.
// The invite endpoint takes `UserAccountInviteCreate`, which is the same shape minus
// `accountContact`, so it is typed inline at its own call site rather than shared here.
interface UserAccountAssociationRequest {
  accountId: number
  admin: boolean
  autoAddLocations: boolean
  autoAddAlerts: boolean
  accountContact: boolean
  invoice: boolean
  position: ClientUserRole
}

const accountUsersQueryKey = (accountId: string) =>
  [...ACCOUNT_USERS_QUERY_KEY, accountId] as const

const accountInvitationsQueryKey = (accountId: string) =>
  [...ACCOUNT_INVITATIONS_QUERY_KEY, accountId] as const

const userAlertSubsQueryKey = (userId: string) =>
  [...USER_ALERT_SUBS_QUERY_KEY, userId] as const

const userQueryKey = (userId: string) => [...USER_QUERY_KEY, userId] as const

const mapUserResponse = (response: RawUserResponse): User => mapUser(response) as User

const mapUserSummary = (user: UserSummaryResponse): User => {
  const firstname = user.firstName ?? ''
  const lastname = user.lastName ?? ''

  return {
    id: String(user.id),
    firstname,
    lastname,
    initials: `${firstname[0] ?? ''}${lastname[0] ?? ''}`,
    email: user.email,
    mobile: '',
    active: user.active,
    groups: [],
    accountIds: [],
    adminAccountIds: [],
    customerLocationIds: [],
    viewAllLocationsAccountIds: [],
    adminCustomerLocationIds: [],
    alertLocationIds: [],
    customerLocations: [],
    accounts: [],
    preferences: { uom: '' },
    accountContactIds: [],
  }
}

const mapAlertSubscription = (subscription: AlertSubscriptionResponse): AlertSub => ({
  id: subscription.id,
  userId: subscription.user_id,
  email: subscription.email,
  locationId: subscription.location_id,
  recipientType: subscription.recipient_type,
})

const replaceGlobalUser = (queryClient: QueryClient, user: User) => {
  queryClient.setQueryData<User[]>(USERS_QUERY_KEY, (users) =>
    users?.map((currentUser) => (currentUser.id === user.id ? user : currentUser)),
  )
}

const invalidateAccountUsers = async (queryClient: QueryClient, accountIds?: string[]) => {
  if (!accountIds) {
    await queryClient.invalidateQueries({ queryKey: ACCOUNT_USERS_QUERY_KEY })
    return
  }

  await Promise.all(
    accountIds.map((accountId) =>
      queryClient.invalidateQueries({
        queryKey: accountUsersQueryKey(accountId),
        exact: true,
      }),
    ),
  )
}

const showError = (error: unknown) =>
  notifyApiError(error)

export const useUsersQuery = () => {
  const authenticated = useAuthStore((state) => state.authenticated)
  const permissions = useAuthStore((state) => state.permissions)

  return useQuery<User[]>({
    queryKey: USERS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<UserSummaryResponse[]>('/users', {
        params: { queryType: 'short' },
        signal,
      })
      return response.data.map(mapUserSummary)
    },
    enabled: authenticated && hasPermissions(permissions, ['READ:USER']),
    staleTime: Infinity,
  })
}

// A single user, for the edit modal. This was a bare `backend.get` inside a `useEffect`:
// nothing deduplicated it (React 18 fires effects twice in dev, so it always went out
// twice), nothing cancelled it when the modal switched users, so a slow response could
// land after a newer one and overwrite it, and failures reached `console.error` only.
//
// `staleTime: 0` + `refetchOnMount: 'always'` keeps the previous behaviour of reading the
// user fresh every time the modal opens.
export const useUserQuery = (userId: string | null, enabled = true) =>
  useQuery<User>({
    queryKey: userQueryKey(userId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await backend.get<RawUserResponse>(`/users/${userId}`, { signal })
      return mapUserResponse(response.data)
    },
    enabled: enabled && userId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  })

export const useAccountUsersQuery = (accountId: string | null, enabled = true) =>
  useQuery<User[]>({
    queryKey: accountUsersQueryKey(accountId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await backend.get<RawUserResponse[]>(`/accounts/${accountId}/users`, { signal })
      return response.data.map(mapUserResponse)
    },
    enabled: enabled && accountId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  })

export const useAccountInvitationsQuery = (accountId: string | null, enabled = true) =>
  useQuery<UserInvite[]>({
    queryKey: accountInvitationsQueryKey(accountId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await backend.get<InvitationResponse[]>(
        `/accounts/${accountId}/invite_user`,
        { signal },
      )
      return response.data.map(mapInvitation)
    },
    enabled: enabled && accountId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  })

export const useUserAlertSubsQuery = (userId: string | null, enabled = true) =>
  useQuery<AlertSub[]>({
    queryKey: userAlertSubsQueryKey(userId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await backend.get<AlertSubscriptionResponse[]>(`/users/${userId}/alert_subs`, {
        signal,
      })
      return response.data.map(mapAlertSubscription)
    },
    enabled: enabled && userId !== null,
  })

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      user,
      accounts,
      autoAddLocations = false,
      isAdmin = false,
      position,
      groupIds = [],
    }: {
      user: User
      accounts: Account[]
      autoAddLocations?: boolean
      isAdmin?: boolean
      position: ClientUserRole
      // Group *ids*, not the names `User.groups` carries. The schema takes `groupIds`,
      // and passing names through would be silently dropped as an unknown field.
      groupIds?: string[]
    }) =>
      backend.post('/users', {
        active: true,
        email: user.email,
        firstName: user.firstname,
        lastName: user.lastname,
        mobile: user.mobile,
        groupIds,
        accounts: accounts.map(
          (account): UserAccountAssociationRequest => ({
            accountId: Number(account.id),
            admin: isAdmin,
            autoAddLocations,
            autoAddAlerts: false,
            accountContact: false,
            invoice: false,
            position,
          }),
        ),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY }),
        invalidateAccountUsers(queryClient),
      ])
    },
    onError: showError,
  })
}

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (user: User) => {
      // No `email`: `UserUpdateAdmin` does not declare one, so sending it only looked
      // like an email change. The modal's email input is already disabled for an
      // existing user, so nothing user-facing depended on it.
      const response = await backend.patch<RawUserResponse>(`/users/${user.id}`, {
        firstName: user.firstname,
        lastName: user.lastname,
        mobile: user.mobile,
        active: user.active,
      })
      return mapUserResponse(response.data)
    },
    // No list refetch here, and none in the other hooks UserEditModal batches (account,
    // groups, locations). The modal sends them in parallel, so each one invalidating
    // cancelled the refetch the previous one had started. The modal refetches once, after the
    // whole save settles.
    onSuccess: (user) => {
      replaceGlobalUser(queryClient, user)
    },
  })
}

// Detaches a user from every account they belong to. The API has no endpoint that deletes
// a user record — `DELETE /users/{id}/accounts` ("Remove User From Account(s) By Id") is the
// closest thing, and removing every association is what makes the user disappear from the
// dashboard. It returns the updated user rather than nothing, so the caches are replaced
// rather than only invalidated.
//
// `useRemoveUserFromAccountMutation` covers the single-account case and is the one wired up
// today; this differs only in removing all of them at once.
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ user }: { user: User }) => {
      const response = await backend.delete<RawUserResponse>(`/users/${user.id}/accounts`, {
        data: { ids: user.accountIds.map(Number) },
      })
      return {
        accountIds: user.accountIds,
        user: mapUserResponse(response.data),
      }
    },
    onSuccess: async ({ accountIds, user }) => {
      replaceGlobalUser(queryClient, user)
      await invalidateAccountUsers(queryClient, accountIds)
      notifySuccess('User removed from all accounts successfully')
    },
    onError: showError,
  })
}

export const useAddUserToAccountsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      user,
      accountIds,
      autoAddLocations = false,
      isAdmin = false,
      position,
    }: {
      user: User
      accountIds: string[]
      autoAddLocations?: boolean
      isAdmin?: boolean
      position: ClientUserRole
    }) => {
      const associations: UserAccountAssociationRequest[] = accountIds.map((accountId) => ({
        accountId: Number(accountId),
        admin: isAdmin,
        autoAddLocations,
        autoAddAlerts: false,
        accountContact: false,
        invoice: false,
        position,
      }))
      const response = await backend.put<RawUserResponse>(`/users/${user.id}/accounts`, associations)
      return {
        accountIds,
        user: mapUserResponse(response.data),
      }
    },
    onSuccess: async ({ accountIds, user }) => {
      replaceGlobalUser(queryClient, user)
      await invalidateAccountUsers(queryClient, accountIds)
    },
    onError: showError,
  })
}

export const useRemoveUserFromAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ user, accountId }: { user: User; accountId: string }) => {
      const response = await backend.delete<RawUserResponse>(`/users/${user.id}/accounts`, {
        data: { ids: [Number(accountId)] },
      })
      return {
        accountId,
        user: mapUserResponse(response.data),
      }
    },
    onSuccess: async ({ accountId, user }) => {
      replaceGlobalUser(queryClient, user)
      await invalidateAccountUsers(queryClient, [accountId])
      notifySuccess('User removed from account successfully')
    },
    onError: showError,
  })
}

export const useUpdateUserAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, accounts }: { userId: string; accounts: UserAccountAssoc[] }) => {
      const response = await backend.patch<RawUserResponse>(
        `/users/${userId}/accounts`,
        accounts.map(
          (account): UserAccountAssociationRequest => ({
            accountId: Number(account.accountId),
            admin: account.admin,
            autoAddLocations: account.autoAddLocations,
            autoAddAlerts: account.autoAddAlerts,
            accountContact: account.accountContact,
            invoice: account.invoice,
            position: account.position,
          }),
        ),
      )
      return {
        accountIds: accounts.map((account) => account.accountId),
        user: mapUserResponse(response.data),
      }
    },
    // See useUpdateUserMutation: UserEditModal refetches the lists once after the save.
    onSuccess: ({ user }) => {
      replaceGlobalUser(queryClient, user)
    },
  })
}

// The groups endpoints return no user to write into the cache, so these have no onSuccess:
// UserEditModal, their only caller, refetches the lists once after the save.
export const useAddUserToGroupsMutation = () =>
  useMutation({
    mutationFn: async ({ user, groupIds }: { user: User; groupIds: string[] }) =>
      backend.patch(`/users/${user.id}/groups`, { ids: groupIds }),
  })

export const useRemoveUserFromGroupsMutation = () =>
  useMutation({
    mutationFn: async ({ user, groupIds }: { user: User; groupIds: string[] }) =>
      backend.delete(`/users/${user.id}/groups`, { data: { ids: groupIds } }),
  })

const useUserLocationMutation = (
  request: (userId: string, locations: UserLocation[]) => Promise<RawUserResponse>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, locations }: { userId: string; locations: UserLocation[] }) => {
      const response = await request(userId, locations)
      return mapUserResponse(response)
    },
    // See useUpdateUserMutation: UserEditModal refetches the lists once after the save.
    onSuccess: (user) => {
      replaceGlobalUser(queryClient, user)
    },
  })
}

export const useAddUserLocationMutation = () =>
  useUserLocationMutation(async (userId, locations) => {
    const response = await backend.put<RawUserResponse>(
      `/users/${userId}/customer_locations`,
      locations.map((location) => ({
        locationIdRef: location.locationId,
        admin: location.admin,
      })),
    )
    return response.data
  })

export const useRemoveUserLocationMutation = () =>
  useUserLocationMutation(async (userId, locations) => {
    const response = await backend.delete<RawUserResponse>(`/users/${userId}/customer_locations`, {
      // `locationIds` is a FastAPI Body(alias=...) embed, which is alias-only — unlike
      // the JSON body models, it does not also accept the snake_case field name.
      data: { locationIds: locations.map((location) => location.locationId) },
    })
    return response.data
  })

export const useUpdateUserLocationMutation = () =>
  useUserLocationMutation(async (userId, locations) => {
    const response = await backend.patch<RawUserResponse>(
      `/users/${userId}/customer_locations`,
      locations.map((location) => ({
        locationIdRef: location.locationId,
        admin: location.admin,
        emergencyContact: location.emergencyContact,
      })),
    )
    return response.data
  })

export const useInviteUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      user,
      account,
      locations,
      welcomeEmail,
      autoAddLocations,
      isAdmin,
      autoAddAlerts,
      accountInvoice,
      position,
      groupIds,
    }: {
      user: User
      account: Account
      locations: InviteLocation[]
      welcomeEmail: boolean
      autoAddLocations: boolean
      isAdmin: boolean
      autoAddAlerts: boolean
      accountInvoice: boolean
      position: ClientUserRole
      // Group *ids*. Previously read off `user.groups`, which the caller had to fill
      // with ids rather than the names that field holds everywhere else.
      groupIds: string[]
    }) => {
      // `accounts` is a single object here, not a list — POST /users/invite_user takes
      // one `UserAccountInviteCreate`, while PATCH /users/invite_user/{id} takes a list.
      const request: {
        email: string
        groupIds: string[]
        accounts: Omit<UserAccountAssociationRequest, 'accountContact'>
        locations?: Array<{
          locationIdRef: string
          admin: boolean
          alerts: boolean
          emergencyContact: boolean
        }>
        welcomeEmail: boolean
      } = {
        email: user.email,
        groupIds,
        accounts: {
          accountId: Number(account.id),
          admin: isAdmin,
          autoAddLocations,
          autoAddAlerts,
          invoice: accountInvoice,
          position,
        },
        welcomeEmail,
      }

      if (locations.length > 0) {
        request.locations = locations.map((location) => ({
          locationIdRef: location.locationId,
          admin: location.admin,
          alerts: location.alerts,
          emergencyContact: location.emergencyContact,
        }))
      }

      return backend.post('/users/invite_user', request)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_INVITATIONS_QUERY_KEY })
      notifySuccess('User invited successfully')
    },
    onError: showError,
  })
}

export const useUpdateUserInviteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitation: UserInvite) => {
      const response = await backend.patch<InvitationResponse>(`/users/invite_user/${invitation.id}`, {
        active: invitation.active,
        // `accounts` is a list here — unlike POST /users/invite_user, which takes one
        // object — and each entry is keyed `accountIdRef`, not `accountId`.
        accounts: invitation.accounts.map((account) => ({
          accountIdRef: account.accountId,
          admin: account.admin,
          autoAddAlerts: account.autoAddAlerts,
          autoAddLocations: account.autoAddLocations,
          invoice: account.invoice,
          position: account.position,
        })),
        welcomeEmail: invitation.welcomeEmail,
        locations: invitation.locations?.map((location) => ({
          locationIdRef: location.locationId,
          admin: location.admin,
          alerts: location.alerts,
          emergencyContact: location.emergencyContact,
        })),
        groupIds: invitation.groupIds,
      })
      return mapInvitation(response.data)
    },
    // The PATCH returns the updated invitation, locations included. It used to be
    // discarded, leaving the UI waiting on a refetch to show a change the server had
    // already confirmed. Write it into every cached account-invitations list first, then
    // invalidate as a backstop.
    onSuccess: async (updated) => {
      queryClient.setQueriesData<UserInvite[]>(
        { queryKey: ACCOUNT_INVITATIONS_QUERY_KEY },
        (invitations) =>
          invitations?.map((invitation) =>
            invitation.id === updated.id ? updated : invitation,
          ),
      )
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_INVITATIONS_QUERY_KEY })
      notifySuccess('User invitation updated successfully')
    },
    onError: showError,
  })
}

export const useResendUserInviteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: number) => backend.put(`/users/resend_invite/${invitationId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_INVITATIONS_QUERY_KEY })
      notifySuccess('Invitation resent successfully')
    },
    onError: showError,
  })
}

export const useAddAlertSubscriptionMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ locationId, user }: { locationId: string; user: User }) =>
      backend.post(`/customer_locations/${locationId}/alert_recipients`, {
        recipients: [{ id: user.id, recipientType: 'to' }],
      }),
    onSuccess: async (_response, { user }) => {
      await queryClient.invalidateQueries({
        queryKey: userAlertSubsQueryKey(user.id),
        exact: true,
      })
    },
    onError: showError,
  })
}

export const useRemoveAlertSubscriptionMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ locationId, user }: { locationId: string; user: User }) =>
      backend.delete(`/customer_locations/${locationId}/alert_recipients`, {
        data: { ids: [user.id] },
      }),
    onSuccess: async (_response, { user }) => {
      await queryClient.invalidateQueries({
        queryKey: userAlertSubsQueryKey(user.id),
        exact: true,
      })
    },
    onError: showError,
  })
}

export type { Group }
